/**
 * Generated by orval 🍺
 * Do not edit manually.
 * DevSMS API v2
 * The dashboard's API (LLD §26.2), and the source Orval generates the
 * dashboard's client from (`dashboard/orval.config.ts`).
 *
 * This document describes the Go server as built. It is held to the code
 * by `test/contract`: the routes it lists are exactly the routes served,
 * in normal and in standalone mode (`TestV2Routes_MatchTheSpecInBothModes`),
 * and every answer the integration, contract and end-to-end tests provoke
 * is validated against the operation it documents
 * (`test/harness/apispec.Validating`). Spectral lints it with
 * `api/.spectral.yaml` (`make spec`).
 *
 * Conventions:
 *   - JSON is camelCase; enums are snake_case; times are RFC 3339 UTC.
 *   - Money is a whole number of XAF (ISO 4217 exponent 0) in a plain
 *     integer field, with a sibling `currency` field (always `XAF`) on
 *     the objects that carry one. There is no `{amount, currency}`
 *     wrapper object (DV-145).
 *   - Lists that can grow are keyset-paged: `?cursor=&limit=` in,
 *     `{items, nextCursor, hasMore}` out. A cursor is opaque and signed;
 *     a tampered one answers 422 `invalid_cursor`. Short, bounded lists
 *     answer `{items}`.
 *   - Errors are RFC 9457 `application/problem+json`. The machine code is
 *     the last segment of `type`, in kebab case
 *     (`…/problems/wallet-insufficient` is the code `wallet_insufficient`);
 *     `x-problem-codes` lists every code with its status. There is no
 *     separate `code` member (DV-145).
 *   - Authentication is a bearer access token from `POST /auth/login`
 *     (or Google, TOTP, refresh, invite). API keys are not accepted on
 *     v2; they are the v1 credential.
 *   - `x-permission` names the permission an operation requires (every
 *     one listed, when it is a list). Account routes (`/account/*`) also
 *     admit any staff user without it (DV-55). Every `/admin/*` route
 *     also refuses a staff user who has not enrolled TOTP (403
 *     `totp_enrolment_required`).
 *   - `x-step-up: true` marks an operation that needs an `X-Step-Up`
 *     token from `POST /auth/step-up`, under 5 minutes old (403
 *     `step_up_required` otherwise).
 *   - `x-idempotency: required` marks an operation that needs an
 *     `Idempotency-Key` header (422 `idempotency_key_required`); a
 *     repeat replays the first answer byte for byte with
 *     `Idempotent-Replay: true`. `x-idempotency: exempt` marks a write
 *     in a money area that deliberately takes none, and
 *     `x-idempotency-reason` says why.
 *   - `x-standalone: absent` marks an operation that is not mounted when
 *     `STANDALONE_MODE=true` (it answers 404 there).
 *   - `?lang=fr|en` (or `Accept-Language`) chooses the language of every
 *     problem's `detail` and of the PDFs; French is the default.
 *   - CORS admits one origin, the deployment's `DASHBOARD_URL`, with
 *     credentials (none when it is unset; never `*`). Preflights are
 *     answered `204` before authentication and rate limiting. Allowed
 *     request headers: `Authorization`, `Content-Type`,
 *     `Accept-Language`, `Idempotency-Key`, `X-Step-Up`, `X-Sdk-Client`;
 *     exposed: `Idempotent-Replay`, `RateLimit-Limit`,
 *     `RateLimit-Remaining`, `RateLimit-Reset`, `Retry-After`,
 *     `X-Request-Id`, `Content-Disposition`. (`/api/v1` keeps Nest's
 *     `enableCors()`: `Access-Control-Allow-Origin: *`, no credentials.)
 *
 * OpenAPI spec version: 2.0.0
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseInfiniteQueryResult,
  DefinedUseQueryResult,
  InfiniteData,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import type {
  AdminGetOverviewParams,
  AdminListAuditParams,
  AdminListJobsParams,
  AdminOverview,
  AuditPage,
  ConflictResponse,
  ForbiddenResponse,
  GetOverviewParams,
  InternalErrorResponse,
  JobDetail,
  JobPage,
  JobSummary,
  NotFoundResponse,
  Overview,
  SystemStatus,
  UnauthorizedResponse,
  UnavailableResponse,
  UnprocessableResponse
} from '../model';

import { apiFetch } from '../../mutator.ts';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

export const getGetOverviewUrl = (params: GetOverviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/overview?${stringifiedParams}` : `/overview`
}

/**
 * The wallet block is left out in standalone mode.
 * @summary The caller's dashboard home figures
 */
export const getOverview = async (params: GetOverviewParams, options?: Parameters<typeof apiFetch>[1]): Promise<Overview> => {

  return apiFetch<Overview>(getGetOverviewUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getGetOverviewQueryKey = (params?: GetOverviewParams,) => {
    return [
    `/overview`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetOverviewQueryOptions = <TData = Awaited<ReturnType<typeof getOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params: GetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOverview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOverviewQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOverview>>> = ({ signal }) => getOverview(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOverview>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof getOverview>>>
export type GetOverviewQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useGetOverview<TData = Awaited<ReturnType<typeof getOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: GetOverviewParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOverview>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOverview>>,
          TError,
          Awaited<ReturnType<typeof getOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOverview<TData = Awaited<ReturnType<typeof getOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: GetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOverview>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOverview>>,
          TError,
          Awaited<ReturnType<typeof getOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOverview<TData = Awaited<ReturnType<typeof getOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: GetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOverview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The caller's dashboard home figures
 */

export function useGetOverview<TData = Awaited<ReturnType<typeof getOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: GetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOverview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetOverviewQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListJobsUrl = (params?: AdminListJobsParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/jobs?${stringifiedParams}` : `/admin/jobs`
}

/**
 * @summary Background jobs, newest first
 */
export const adminListJobs = async (params?: AdminListJobsParams, options?: Parameters<typeof apiFetch>[1]): Promise<JobPage> => {

  return apiFetch<JobPage>(getAdminListJobsUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListJobsInfiniteQueryKey = (params?: AdminListJobsParams,) => {
    return [
    'infinite', `/admin/jobs`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListJobsQueryKey = (params?: AdminListJobsParams,) => {
    return [
    `/admin/jobs`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListJobsInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListJobs>>, AdminListJobsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListJobsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData, QueryKey, AdminListJobsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListJobsInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListJobs>>, QueryKey, AdminListJobsParams['cursor']> = ({ signal, pageParam }) => adminListJobs({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData, QueryKey, AdminListJobsParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListJobsInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListJobs>>>
export type AdminListJobsInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListJobsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListJobs>>, AdminListJobsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListJobsParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData, QueryKey, AdminListJobsParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListJobs>>,
          TError,
          Awaited<ReturnType<typeof adminListJobs>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListJobsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListJobs>>, AdminListJobsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListJobsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData, QueryKey, AdminListJobsParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListJobs>>,
          TError,
          Awaited<ReturnType<typeof adminListJobs>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListJobsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListJobs>>, AdminListJobsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListJobsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData, QueryKey, AdminListJobsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Background jobs, newest first
 */

export function useAdminListJobsInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListJobs>>, AdminListJobsParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListJobsParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData, QueryKey, AdminListJobsParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListJobsInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListJobsQueryOptions = <TData = Awaited<ReturnType<typeof adminListJobs>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListJobsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListJobsQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListJobs>>> = ({ signal }) => adminListJobs(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListJobsQueryResult = NonNullable<Awaited<ReturnType<typeof adminListJobs>>>
export type AdminListJobsQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListJobs<TData = Awaited<ReturnType<typeof adminListJobs>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListJobsParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListJobs>>,
          TError,
          Awaited<ReturnType<typeof adminListJobs>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListJobs<TData = Awaited<ReturnType<typeof adminListJobs>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListJobsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListJobs>>,
          TError,
          Awaited<ReturnType<typeof adminListJobs>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListJobs<TData = Awaited<ReturnType<typeof adminListJobs>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListJobsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Background jobs, newest first
 */

export function useAdminListJobs<TData = Awaited<ReturnType<typeof adminListJobs>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListJobsParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListJobs>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListJobsQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminGetJobUrl = (id: string,) => {




  return `/admin/jobs/${id}`
}

/**
 * @summary One job, its arguments redacted and its errors
 */
export const adminGetJob = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<JobDetail> => {

  return apiFetch<JobDetail>(getAdminGetJobUrl(id),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetJobQueryKey = (id: string,) => {
    return [
    `/admin/jobs/${id}`
    ] as const;
    }


export const getAdminGetJobQueryOptions = <TData = Awaited<ReturnType<typeof adminGetJob>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetJob>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetJobQueryKey(id);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetJob>>> = ({ signal }) => adminGetJob(id, { signal, ...requestOptions });





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetJob>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetJobQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetJob>>>
export type AdminGetJobQueryError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetJob<TData = Awaited<ReturnType<typeof adminGetJob>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetJob>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetJob>>,
          TError,
          Awaited<ReturnType<typeof adminGetJob>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetJob<TData = Awaited<ReturnType<typeof adminGetJob>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetJob>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetJob>>,
          TError,
          Awaited<ReturnType<typeof adminGetJob>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetJob<TData = Awaited<ReturnType<typeof adminGetJob>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetJob>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary One job, its arguments redacted and its errors
 */

export function useAdminGetJob<TData = Awaited<ReturnType<typeof adminGetJob>>, TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | InternalErrorResponse | UnavailableResponse>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetJob>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetJobQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminRetryJobUrl = (id: string,) => {




  return `/admin/jobs/${id}/retry`
}

/**
 * Only a discarded job, whose work no live twin already does (409
 * `job_not_discarded`, `job_superseded`, `notice_finished`).
 * @summary Run a discarded job again
 */
export const adminRetryJob = async (id: string, options?: Parameters<typeof apiFetch>[1]): Promise<JobSummary> => {

  return apiFetch<JobSummary>(getAdminRetryJobUrl(id),
  {
    ...options,
    method: 'POST'


  }
);}





export const getAdminRetryJobMutationKey = () => ['adminRetryJob'] as const;

export const getAdminRetryJobMutationOptions = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminRetryJob>>, TError,AdminRetryJobMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
): UseMutationOptions<Awaited<ReturnType<typeof adminRetryJob>>, TError,AdminRetryJobMutationVariables, TContext> => {

const mutationKey = getAdminRetryJobMutationKey();
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof adminRetryJob>>, AdminRetryJobMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  adminRetryJob(id,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type AdminRetryJobMutationResult = NonNullable<Awaited<ReturnType<typeof adminRetryJob>>>

    export type AdminRetryJobMutationError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse
    export type AdminRetryJobMutationVariables = {id: string}

    /**
 * @summary Run a discarded job again
 */
export const useAdminRetryJob = <TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse | ConflictResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof adminRetryJob>>, TError,AdminRetryJobMutationVariables, TContext>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof adminRetryJob>>,
        TError,
        AdminRetryJobMutationVariables,
        TContext
      > => {
      return useMutation(getAdminRetryJobMutationOptions(options), queryClient);
    }
    export const getAdminGetSystemStatusUrl = () => {




  return `/admin/system/status`
}

/**
 * Always 200; each component carries its own status. `operators` is left out in standalone mode.
 * @summary Each component's health
 */
export const adminGetSystemStatus = async ( options?: Parameters<typeof apiFetch>[1]): Promise<SystemStatus> => {

  return apiFetch<SystemStatus>(getAdminGetSystemStatusUrl(),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetSystemStatusQueryKey = () => {
    return [
    `/admin/system/status`
    ] as const;
    }


export const getAdminGetSystemStatusQueryOptions = <TData = Awaited<ReturnType<typeof adminGetSystemStatus>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetSystemStatus>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetSystemStatusQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetSystemStatus>>> = ({ signal }) => adminGetSystemStatus({ signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetSystemStatus>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetSystemStatusQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetSystemStatus>>>
export type AdminGetSystemStatusQueryError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetSystemStatus<TData = Awaited<ReturnType<typeof adminGetSystemStatus>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetSystemStatus>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetSystemStatus>>,
          TError,
          Awaited<ReturnType<typeof adminGetSystemStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetSystemStatus<TData = Awaited<ReturnType<typeof adminGetSystemStatus>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetSystemStatus>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetSystemStatus>>,
          TError,
          Awaited<ReturnType<typeof adminGetSystemStatus>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetSystemStatus<TData = Awaited<ReturnType<typeof adminGetSystemStatus>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetSystemStatus>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Each component's health
 */

export function useAdminGetSystemStatus<TData = Awaited<ReturnType<typeof adminGetSystemStatus>>, TError = UnauthorizedResponse | ForbiddenResponse | InternalErrorResponse | UnavailableResponse>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetSystemStatus>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetSystemStatusQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListAuditUrl = (params?: AdminListAuditParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/audit?${stringifiedParams}` : `/admin/audit`
}

/**
 * @summary The audit log, newest first
 */
export const adminListAudit = async (params?: AdminListAuditParams, options?: Parameters<typeof apiFetch>[1]): Promise<AuditPage> => {

  return apiFetch<AuditPage>(getAdminListAuditUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminListAuditInfiniteQueryKey = (params?: AdminListAuditParams,) => {
    return [
    'infinite', `/admin/audit`, ...(params ? [params] : [])
    ] as const;
    }

export const getAdminListAuditQueryKey = (params?: AdminListAuditParams,) => {
    return [
    `/admin/audit`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminListAuditInfiniteQueryOptions = <TData = InfiniteData<Awaited<ReturnType<typeof adminListAudit>>, AdminListAuditParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListAuditParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData, QueryKey, AdminListAuditParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListAuditInfiniteQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListAudit>>, QueryKey, AdminListAuditParams['cursor']> = ({ signal, pageParam }) => adminListAudit({...params, ...((c) => c === undefined ? {} : { 'cursor': c })(pageParam ?? params?.['cursor'])}, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData, QueryKey, AdminListAuditParams['cursor']> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListAuditInfiniteQueryResult = NonNullable<Awaited<ReturnType<typeof adminListAudit>>>
export type AdminListAuditInfiniteQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListAuditInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListAudit>>, AdminListAuditParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListAuditParams, options: { query:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData, QueryKey, AdminListAuditParams['cursor']>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListAudit>>,
          TError,
          Awaited<ReturnType<typeof adminListAudit>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListAuditInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListAudit>>, AdminListAuditParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListAuditParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData, QueryKey, AdminListAuditParams['cursor']>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListAudit>>,
          TError,
          Awaited<ReturnType<typeof adminListAudit>>, QueryKey
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListAuditInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListAudit>>, AdminListAuditParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListAuditParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData, QueryKey, AdminListAuditParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The audit log, newest first
 */

export function useAdminListAuditInfinite<TData = InfiniteData<Awaited<ReturnType<typeof adminListAudit>>, AdminListAuditParams['cursor']>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListAuditParams, options?: { query?:Partial<UseInfiniteQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData, QueryKey, AdminListAuditParams['cursor']>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListAuditInfiniteQueryOptions(params,options)

  const query = useInfiniteQuery(queryOptions, queryClient) as  UseInfiniteQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminListAuditQueryOptions = <TData = Awaited<ReturnType<typeof adminListAudit>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params?: AdminListAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminListAuditQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminListAudit>>> = ({ signal }) => adminListAudit(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminListAuditQueryResult = NonNullable<Awaited<ReturnType<typeof adminListAudit>>>
export type AdminListAuditQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminListAudit<TData = Awaited<ReturnType<typeof adminListAudit>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: undefined |  AdminListAuditParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListAudit>>,
          TError,
          Awaited<ReturnType<typeof adminListAudit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListAudit<TData = Awaited<ReturnType<typeof adminListAudit>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminListAudit>>,
          TError,
          Awaited<ReturnType<typeof adminListAudit>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminListAudit<TData = Awaited<ReturnType<typeof adminListAudit>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The audit log, newest first
 */

export function useAdminListAudit<TData = Awaited<ReturnType<typeof adminListAudit>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params?: AdminListAuditParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminListAudit>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminListAuditQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






export const getAdminGetOverviewUrl = (params: AdminGetOverviewParams,) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {

    if (value !== undefined) {
      normalizedParams.append(key, value === null ? 'null' : String(value))
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/admin/overview?${stringifiedParams}` : `/admin/overview`
}

/**
 * @summary The admin home figures
 */
export const adminGetOverview = async (params: AdminGetOverviewParams, options?: Parameters<typeof apiFetch>[1]): Promise<AdminOverview> => {

  return apiFetch<AdminOverview>(getAdminGetOverviewUrl(params),
  {
    ...options,
    method: 'GET'


  }
);}





export const getAdminGetOverviewQueryKey = (params?: AdminGetOverviewParams,) => {
    return [
    `/admin/overview`, ...(params ? [params] : [])
    ] as const;
    }


export const getAdminGetOverviewQueryOptions = <TData = Awaited<ReturnType<typeof adminGetOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(params: AdminGetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetOverview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getAdminGetOverviewQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof adminGetOverview>>> = ({ signal }) => adminGetOverview(params, { signal, ...requestOptions });





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof adminGetOverview>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type AdminGetOverviewQueryResult = NonNullable<Awaited<ReturnType<typeof adminGetOverview>>>
export type AdminGetOverviewQueryError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse


export function useAdminGetOverview<TData = Awaited<ReturnType<typeof adminGetOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: AdminGetOverviewParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetOverview>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetOverview>>,
          TError,
          Awaited<ReturnType<typeof adminGetOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetOverview<TData = Awaited<ReturnType<typeof adminGetOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: AdminGetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetOverview>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof adminGetOverview>>,
          TError,
          Awaited<ReturnType<typeof adminGetOverview>>
        > , 'initialData'
      >, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useAdminGetOverview<TData = Awaited<ReturnType<typeof adminGetOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: AdminGetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetOverview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary The admin home figures
 */

export function useAdminGetOverview<TData = Awaited<ReturnType<typeof adminGetOverview>>, TError = UnauthorizedResponse | ForbiddenResponse | UnprocessableResponse | InternalErrorResponse | UnavailableResponse>(
 params: AdminGetOverviewParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof adminGetOverview>>, TError, TData>>, request?: SecondParameter<typeof apiFetch>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getAdminGetOverviewQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






